import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons/faArrowsRotate";
import { faEdit } from "@fortawesome/free-solid-svg-icons/faEdit";
import { faLinkSlash } from "@fortawesome/free-solid-svg-icons/faLinkSlash";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraEmpty } from "../../../components/AtiraEmpty";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { Button } from "../../../components/Button";
import { Drawer } from "../../../components/Drawer";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { WarningModal } from "../../../components/WarningModal";
import { IntegrationConfigFacebookMetaForm } from "../../../model/integration/types/config/IntegrationConfigFacebookMetaForm";
import { formSliceSelectors } from "../../../redux/forms/form.selector";
import { formActions } from "../../../redux/forms/form.slice";
import { integrationSliceSelectors } from "../../../redux/integration/integration.selector";
import { integrationActions } from "../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { EntityIcons } from "../../../utils/GlobalIcons";
import { Tooltip } from "../../../utils/local-antd";
import { useIntegrationContext } from "../context/integration-context";

const IconButton = styled(Button)`
  background-color: transparent;
  padding: 0;
  color: ${({ theme }) => theme.main};
`;

const LinkedFormRow = styled(Flex)`
  align-items: center;
  gap: ${Spacing.m};
  padding: ${Spacing.s} ${Spacing.m};
  border-radius: ${Rounded.md};
  background-color: ${({ theme }) => theme.lightSub};
`;

type Props = {
  open: boolean;
  onClose: VoidFunction;
};

export const IntegrationFacebookMetaFormsDrawer: React.FC<Props> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { setAddModalFacebookMetaVisible, setFacebookMetaEditFormId } =
    useIntegrationContext();

  const [unlinking, setUnlinking] =
    useState<IntegrationConfigFacebookMetaForm | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchingFormId, setFetchingFormId] = useState<string | null>(null);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const integration = useAppSelector(
    integrationSliceSelectors.selectFacebookMetaIntegration,
  );
  const atiraForms = useAppSelector(formSliceSelectors.selectForms);

  useEffect(() => {
    if (open) {
      dispatch(formActions.getForms({ userId }));
    }
  }, [dispatch, open, userId]);

  const linkedForms = integration?.config.forms || [];

  const openLinkModal = (metaFormId: string | null) => {
    setFacebookMetaEditFormId(metaFormId);
    setAddModalFacebookMetaVisible(true);
  };

  const onFetchLeads = async (metaFormId: string) => {
    if (!integration) {
      return;
    }

    try {
      setFetchingFormId(metaFormId);

      const { fetched } = await dispatch(
        integrationActions.fetchFacebookMetaLeads({
          userId,
          integrationId: integration._id,
          metaFormId,
        }),
      ).unwrap();

      if (fetched) {
        AtiraToast.success(
          t("integration.facebook-meta.drawer.fetchLeads.done", {
            count: fetched,
          }),
        );
      } else {
        AtiraToast.info(t("integration.facebook-meta.drawer.fetchLeads.none"));
      }
    } catch (error) {
      AtiraToast.apiError(error);
    } finally {
      setFetchingFormId(null);
    }
  };

  const onUnlink = async () => {
    if (!integration || !unlinking) {
      return;
    }

    try {
      setLoading(true);

      await dispatch(
        integrationActions.unlinkFacebookMetaForm({
          userId,
          integrationId: integration._id,
          metaFormId: unlinking.metaFormId,
        }),
      ).unwrap();
      await dispatch(integrationActions.getUserIntegrations({ userId }));

      setUnlinking(null);
    } catch (error) {
      AtiraToast.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title={t("integration.facebook-meta.drawer.title")}
      open={open}
      onClose={onClose}
      size="large"
      destroyOnHidden
      extra={
        <Tooltip title={t("integration.facebook-meta.section.addForm")}>
          <IconButton
            icon={faPlus}
            iconWidth="xl"
            onClick={() => openLinkModal(null)}
            disabled={integration?.config.needsReconnect}
          />
        </Tooltip>
      }
    >
      {linkedForms.length ? (
        <Flex flexDirection="column" gap="s">
          {linkedForms.map((form) => (
            <LinkedFormRow key={form.metaFormId}>
              <AtiraIcon icon={EntityIcons.Forms} color="main" size="lg" />

              <Flex flexDirection="column">
                <Text>
                  {t("integration.facebook-meta.section.link", {
                    page:
                      integration?.config.pages?.find(
                        ({ id }) => id === form.pageId,
                      )?.name || "-",
                    metaForm: form.metaFormName,
                    form:
                      atiraForms.find(({ _id }) => _id === form.formId)
                        ?.title || "-",
                  })}
                </Text>

                {integration?.config.pages?.some(
                  ({ id }) => id === form.pageId,
                ) ? null : (
                  <Text color="danger">
                    {t("integration.facebook-meta.drawer.pageMissing")}
                  </Text>
                )}
              </Flex>

              <Flex gap="m" alignItems="center" marginInlineStart="auto">
                <Tooltip
                  title={t("integration.facebook-meta.drawer.fetchLeads.title")}
                >
                  <IconButton
                    icon={faArrowsRotate}
                    iconWidth="lg"
                    loading={fetchingFormId === form.metaFormId}
                    disabled={
                      Boolean(fetchingFormId) ||
                      integration?.config.needsReconnect
                    }
                    onClick={() => onFetchLeads(form.metaFormId)}
                  />
                </Tooltip>

                <Tooltip title={t("common.edit")}>
                  <IconButton
                    icon={faEdit}
                    iconWidth="lg"
                    onClick={() => openLinkModal(form.metaFormId)}
                  />
                </Tooltip>

                <Tooltip
                  title={t("integration.facebook-meta.section.unlink.title")}
                >
                  <IconButton
                    icon={faLinkSlash}
                    iconWidth="lg"
                    onClick={() => setUnlinking(form)}
                  />
                </Tooltip>
              </Flex>
            </LinkedFormRow>
          ))}
        </Flex>
      ) : (
        <AtiraEmpty
          description={t("integration.facebook-meta.section.empty")}
        />
      )}

      <WarningModal
        isOpen={Boolean(unlinking)}
        onClose={() => setUnlinking(null)}
        onConfirm={onUnlink}
        loading={loading}
        title={t("integration.facebook-meta.section.unlink.title")}
        description={t("integration.facebook-meta.section.unlink.description", {
          metaForm: unlinking?.metaFormName,
        })}
      />
    </Drawer>
  );
};
